import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal } from "antd";
import Search from "antd/es/transfer/search";
import { toast } from "react-toastify";
import ContentLayout from "../../../layout/ContentLayout";
import { Container } from "../../../components/content";
import TableSection from "../../../components/tables/TableSection";
import Spinner from "../../../components/elements/Spinner/Spinner";
import quizService from "../../../api/quiz-api";
import { useDebounce } from "../../../hooks/useDebounce";
import { formatDate } from "../../../utils/formatDate";
import styles from "../../../styles/mobileInfoPage.module.scss";

export default function KvizRezultati() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [quiz, setQuiz] = useState(null);
    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const debouncedSearch = useDebounce(search, 500);

    const onSearchChange = (val) => {
        setSearch(val);
        setCurrentPage(1);
    };

    const loadQuiz = useCallback(async () => {
        const response = await quizService.getById(id);
        if (response.success) {
            setQuiz(response.data);
        } else {
            toast.error(response.message || 'Greška pri učitavanju kviza');
            navigate('/kvizovi');
        }
    }, [id, navigate]);

    const loadResponses = useCallback(async () => {
        setLoading(true);
        try {
            const response = await quizService.getResponses(id, currentPage, 10, debouncedSearch);
            if (!response.success) {
                toast.error(response.message || 'Greška pri učitavanju rezultata');
                setData([]);
                setTotalPages(1);
                setTotalItems(0);
                return;
            }
            setData(response.data.items || []);
            setTotalPages(response.data.totalPages || 1);
            setTotalItems(response.data.totalItems || 0);
        } finally {
            setLoading(false);
        }
    }, [id, currentPage, debouncedSearch]);

    useEffect(() => {
        loadQuiz();
    }, [loadQuiz]);

    useEffect(() => {
        loadResponses();
    }, [loadResponses]);

    const questionTextById = new Map((quiz?.questions || []).map((q) => [q._id, q.text?.sr]));
    const optionTextById = new Map(
        (quiz?.questions || []).flatMap((q) => (q.options || []).map((o) => [o._id, o.text?.sr])),
    );

    const openDetail = async (row) => {
        setLoadingDetail(true);
        try {
            const response = await quizService.getResponseDetail(id, row.userId);
            if (!response.success) {
                toast.error(response.message || 'Greška pri učitavanju odgovora');
                return;
            }
            setDetail(response.data);
        } finally {
            setLoadingDetail(false);
        }
    };

    const columns = [
        {
            key: 'userName',
            label: 'Korisnik',
            render: (val) => val || '-',
        },
        {
            key: 'userEmail',
            label: 'Email',
            render: (val) => val || '-',
        },
        {
            key: 'score',
            label: 'Tačni odgovori',
            render: (val, row) => `${val} / ${row.totalScoredQuestions}`,
        },
        {
            key: 'pointsAwarded',
            label: 'Osvojeno poena',
            render: (val) => val ?? 0,
        },
        {
            key: 'completedAt',
            label: 'Datum popunjavanja',
            render: (val) => formatDate(val),
        },
    ];

    const actions = [
        {
            key: 'header',
            label: 'Opcije',
            disabled: true,
            className: 'dropdown-header',
        },
        {
            key: 'detail',
            label: 'Prikaži odgovore',
            icon: <img src="/assets/icons/view-icon.svg" alt="Detalji" />,
            onClick: (row) => openDetail(row),
        },
    ];

    return (
        <>
            <Container>
                <Link to="/kvizovi" className="link_btn" style={{ marginBottom: '12px' }}>
                    <img alt="back" src="/assets/icons/chevron.svg" />
                    <span>Nazad</span>
                </Link>

                <ContentLayout header={quiz ? `Rezultati - ${quiz.name}` : 'Rezultati kviza'}>
                    <div style={{ marginBottom: '16px', maxWidth: '360px' }}>
                        <Search
                            placeholder="Pretraži po imenu ili email-u"
                            className="search-input"
                            allowClear
                            enterButton
                            onChange={(e) => onSearchChange(e.target.value)}
                            value={search}
                        />
                    </div>
                    {loading ? (
                        <Spinner size="lg" />
                    ) : (
                        <TableSection
                            columns={columns}
                            actions={actions}
                            data={data}
                            emptyState={{
                                icon: '/assets/icons/no-users.svg',
                                title: debouncedSearch ? 'Nema rezultata za dati pojam pretrage' : 'Kviz još uvek nema odgovora',
                            }}
                            showPagination={data.length > 0}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            onPageChange={setCurrentPage}
                            onSearch={() => {}}
                        />
                    )}
                </ContentLayout>
            </Container>

            <Modal
                open={!!detail}
                onCancel={() => setDetail(null)}
                footer={null}
                title={detail ? `Odgovori - ${detail.userName || detail.userEmail}` : ''}
            >
                {loadingDetail || !detail ? (
                    <Spinner size="md" />
                ) : (
                    <div>
                        <p><strong>Tačni odgovori:</strong> {detail.score} / {detail.totalScoredQuestions}</p>
                        <p><strong>Osvojeno poena:</strong> {detail.pointsAwarded ?? 0}</p>
                        {detail.answers.map((answer, idx) => (
                            <div key={idx} className={styles.section} style={{ marginBottom: '16px' }}>
                                <p><strong>{idx + 1}. {questionTextById.get(answer.questionId) || 'Pitanje'}</strong></p>
                                {answer.answerText !== undefined ? (
                                    <p>Odgovor: {answer.answerText || '-'}</p>
                                ) : (
                                    <p>
                                        Odgovoreno: {answer.selectedOptionIds.map((oid) => optionTextById.get(oid) || oid).join(', ')}
                                        {answer.correctOptionIds.length > 0 && (
                                            <span style={{ color: answer.isCorrect ? '#027A48' : '#B42318', marginLeft: '8px' }}>
                                                ({answer.isCorrect ? 'Tačno' : 'Netačno'})
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Modal>
        </>
    );
}
